import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './pages.css';
import './LoansPage.css';

const LOAN_TYPE_META = {
    'Personal Loan':           { icon: '👤', color: '#0755d9' },
    'Two-Wheeler Loan':        { icon: '🛵', color: '#0ea5e9' },
    'Three-Wheeler Loan':      { icon: '🚐', color: '#f59e0b' },
    'Loan Against Property':   { icon: '🏠', color: '#10b981' },
    'Business Loan':           { icon: '🏢', color: '#6366f1' },
    'Education Loan':          { icon: '🎓', color: '#ec4899' },
};

const getStatusBadge = (status) => {
    const map = {
        Active:   'pg-badge-green',
        Pending:  'pg-badge-amber',
        Approved: 'pg-badge-blue',
        Rejected: 'pg-badge-red',
        Closed:   'pg-badge-neutral',
    };
    return map[status] || 'pg-badge-neutral';
};

const getApprovalBadge = (s) => {
    if (s === 'Admin_Approved') return { cls: 'pg-badge-green',  label: '✓ Approved' };
    if (s === 'KYC_Approved')   return { cls: 'pg-badge-blue',   label: '🔍 Under Review' };
    if (s === 'Rejected')       return { cls: 'pg-badge-red',    label: '✗ Rejected' };
    return                             { cls: 'pg-badge-amber',  label: '⏳ Pending' };
};

const formatDate = (date) => date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
});

const getTenureDueDate = (loan) => {
    const tenureDays = Number(loan.tenure_months || 0);
    const isAwaitingDisbursal = !['Approved', 'Active'].includes(loan.status) &&
        !['Admin_Approved', 'Disbursed'].includes(loan.approval_status);
    const baseDateValue = isAwaitingDisbursal ? null : (loan.disbursement_date || loan.reviewed_at || loan.created_at);
    const baseDate = baseDateValue ? new Date(baseDateValue) : new Date();
    const savedDueDate = loan.due_date ? new Date(loan.due_date) : null;
    const hasSavedDueDate = savedDueDate && !Number.isNaN(savedDueDate.getTime());

    if (!Number.isFinite(tenureDays) || tenureDays <= 0 || Number.isNaN(baseDate.getTime())) {
        return hasSavedDueDate ? formatDate(savedDueDate) : 'N/A';
    }

    const dueDate = new Date(baseDate);
    dueDate.setDate(dueDate.getDate() + tenureDays);
    const effectiveDueDate = hasSavedDueDate && savedDueDate > dueDate ? savedDueDate : dueDate;
    return formatDate(effectiveDueDate);
};

function LoansPage() {
    const navigate = useNavigate();
    const [loans, setLoans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [tab, setTab] = useState('all');
    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchLoans = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (!session?.userId) return setIsLoading(false);
        setIsLoading(true);
        fetch(`${API_URL}/api/loans/${session.userId}`)
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    setLoans(data.loans.map(l => {
                        // Server sends progress per lead/loan stage; derive only when it does not.
                        let computedProgress = l.progress ?? 0;
                        if (l.progress == null && l.status === 'Pending') {
                            if (l.approval_status === 'Admin_Approved') computedProgress = 80;
                            else if (l.approval_status === 'KYC_Approved') computedProgress = 50;
                            else if (l.kyc_status === 'completed' || l.kyc_status === 'verified') computedProgress = 30;
                            else computedProgress = 10;
                        } else if (l.progress == null && l.status === 'Closed') {
                            computedProgress = 100;
                        }
                        return {
                            ...l,
                            progress: computedProgress,
                            displayAmount: Number(l.amount).toLocaleString('en-IN'),
                            displayEmi:    Number(l.emi).toLocaleString('en-IN'),
                            dueDate: getTenureDueDate(l),
                            meta: LOAN_TYPE_META[l.type] || { icon: '💰', color: '#0755d9' },
                        };
                    }));
                }
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, [API_URL]);

    useEffect(() => { fetchLoans(); }, [fetchLoans]);

    const filtered = tab === 'all' ? loans : loans.filter(l => l.status.toLowerCase() === tab);

    const stats = {
        total: loans.length,
        active: loans.filter(l => l.status === 'Active').length,
        pending: loans.filter(l => l.status === 'Pending').length,
        // "Borrowed" = money actually disbursed. Pending / under-review / rejected loans
        // are not borrowed yet, so they must not inflate this total.
        totalAmt: loans
            .filter(l => ['Active', 'Closed'].includes(l.status) || l.approval_status === 'Disbursed')
            .reduce((s, l) => s + Number(l.amount || 0), 0),
    };

    if (isLoading) return (
        <div className="pg-loading"><div className="pg-spinner" /><span>Loading your loans…</span></div>
    );

    return (
        <div className="pg-root ml-root">
            {/* Header */}
            <section className="ml-hero">
                <div className="ml-hero-copy">
                    <span className="ml-hero-icon" aria-hidden="true">💰</span>
                    <div>
                        {/* <span className="ml-hero-kicker">Your loan journey</span> */}
                        <h1>My Loans</h1>
                        <p>View and manage all your loan applications</p>
                    </div>
                </div>
                <div className="ml-hero-art" aria-hidden="true">
                    {/* <span className="ml-art-doc" />
                    <span className="ml-art-doc ml-art-doc-back" />
                    <span className="ml-art-coin">₹</span>
                    <span className="ml-art-chip">₹</span>
                    <span className="ml-art-bars"><b /><b /><b /><b /></span>
                    <svg className="ml-art-trend" viewBox="0 0 120 70">
                        <polyline points="4,64 34,40 58,48 104,10" />
                        <polyline points="88,10 104,10 104,26" />
                    </svg> */}
                </div>
                <button className="ml-hero-btn" onClick={() => navigate('/dashboard/apply-loan')}>
                    <span aria-hidden="true">＋</span> Apply for Loan <span aria-hidden="true">→</span>
                </button>
            </section>

            {/* Stats */}
            <div className="ml-stats">
                <div className="ml-stat tone-blue">
                    <span className="ml-stat-icon" aria-hidden="true">📄</span>
                    <span className="ml-stat-label">Total Loans</span>
                    <span className="ml-stat-value">{stats.total}</span>
                    <span className="ml-stat-art" aria-hidden="true">📂</span>
                </div>
                <div className="ml-stat tone-green">
                    <span className="ml-stat-icon" aria-hidden="true">✅</span>
                    <span className="ml-stat-label">Active</span>
                    <span className="ml-stat-value">{stats.active}</span>
                    <span className="ml-stat-art" aria-hidden="true">💵</span>
                </div>
                <div className="ml-stat tone-amber">
                    <span className="ml-stat-icon" aria-hidden="true">⏳</span>
                    <span className="ml-stat-label">Pending Review</span>
                    <span className="ml-stat-value">{stats.pending}</span>
                    <span className="ml-stat-art" aria-hidden="true">🗂️</span>
                </div>
                <div className="ml-stat tone-violet">
                    <span className="ml-stat-icon" aria-hidden="true">₹</span>
                    <span className="ml-stat-label">Total Borrowed</span>
                    <span className="ml-stat-value">₹{stats.totalAmt.toLocaleString('en-IN')}</span>
                    <span className="ml-stat-art" aria-hidden="true">🪙</span>
                </div>
            </div>

            {/* Loan Cards */}
            <div className="ml-panel">
                <div className="ml-panel-header">
                    <h2>Your Loans</h2>
                    <div className="ml-tabs" role="tablist">
                        {['all','active','pending','rejected'].map(t => (
                            <button key={t}
                                role="tab"
                                aria-selected={tab === t}
                                className={`ml-tab ${tab === t ? 'is-active' : ''}`}
                                onClick={() => setTab(t)}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="ml-empty">
                        <div className="ml-empty-bg" aria-hidden="true">
                            <span className="ml-empty-chip ml-empty-chip-1">📄</span>
                            <span className="ml-empty-chip ml-empty-chip-2">%</span>
                            <span className="ml-empty-chip ml-empty-chip-3">📊</span>
                            <svg className="ml-empty-trend" viewBox="0 0 260 120">
                                <path d="M0 110 C 60 110, 150 112, 190 90 S 240 40, 255 10" />
                                <circle cx="190" cy="90" r="3.5" />
                                <circle cx="255" cy="10" r="5" />
                            </svg>
                        </div>
                        <div className="ml-empty-art" aria-hidden="true">
                            <span className="ml-empty-folder">📁</span>
                            <span className="ml-empty-badge">₹</span>
                            <span className="ml-empty-spark ml-empty-spark-1">✦</span>
                            <span className="ml-empty-spark ml-empty-spark-2">✦</span>
                        </div>
                        <h3>No loans found</h3>
                        <p>You have no {tab !== 'all' ? tab : ''} loans yet. Apply for one to get started.</p>
                        <button className="ml-empty-btn" onClick={() => navigate('/dashboard/apply-loan')}>
                            Apply for a Loan <span aria-hidden="true">→</span>
                        </button>
                    </div>
                ) : (
                    <div className="pg-cards-grid">
                        {filtered.map(loan => {
                            const apv = getApprovalBadge(loan.approval_status);
                            const reviewStatus =
                                loan.status === 'Rejected' ? { label: '❌ Rejected', color: '#b91c1c' }
                                : (loan.status === 'Active' || loan.status === 'Closed') ? { label: `✅ ${loan.stage_label || 'Disbursed'}`, color: '#15803d' }
                                : (loan.approval_status === 'Admin_Approved' || loan.approval_status === 'Disbursed') ? { label: '✅ Approved', color: '#15803d' }
                                : { label: `🕐 ${loan.stage_label || 'Under Review'}`, color: '#b45309' };
                            return (
                                <div key={loan.id} className="pg-card ml-card">
                                    <div className="pg-card-accent" style={{ background: `linear-gradient(90deg, ${loan.meta.color}, #38bdf8)` }} />
                                    <div className="pg-card-header">
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                                                <span style={{ fontSize: '1.3rem' }}>{loan.meta.icon}</span>
                                                <p className="pg-card-title">{loan.type}</p>
                                            </div>
                                            <p className="pg-card-sub">{loan.account_number}</p>
                                        </div>
                                        <span className={`pg-badge ${getStatusBadge(loan.status)}`}>{loan.status}</span>
                                    </div>

                                    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: '0.65rem' }}>
                                        {/* <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Loan Amount</div>
                                            <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1e1b4b', letterSpacing: '-0.5px' }}>₹{loan.displayAmount}</div>
                                        </div> */}

                                        <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Application Status</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: '800', color: reviewStatus.color, letterSpacing: '-0.3px' }}>
                                                {reviewStatus.label}
                                            </div>
                                        </div>

                                        {/* <div className="pg-info-grid">
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Total Repayment</span>
                                                <span className="pg-info-value">₹{loan.displayEmi}</span>
                                            </div>
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Next Due</span>
                                                <span className="pg-info-value">{loan.dueDate}</span>
                                            </div>
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Interest Rate</span>
                                                <span className="pg-info-value">{loan.interest_rate}% per day</span>
                                            </div>
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Tenure</span>
                                                <span className="pg-info-value">{loan.tenure_months} days</span>
                                            </div>
                                        </div> */}

                                        <div className="pg-progress-wrap">
                                            <div className="pg-progress-meta">
                                                <span>Loan Progress</span>
                                                <strong>{loan.progress}%</strong>
                                            </div>
                                            <div className="pg-progress-track">
                                                <div className="pg-progress-fill" style={{ width: `${loan.progress}%` }} />
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span className={`pg-badge ${apv.cls}`}>{apv.label}</span>
                                            <button className="pg-btn pg-btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.78rem' }}
                                                onClick={() => { setSelectedLoan(loan); setShowDetails(true); }}>
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Loan Details Modal */}
            {showDetails && selectedLoan && (
                <div className="pg-modal-overlay" onClick={() => setShowDetails(false)}>
                    <div className="pg-modal pg-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="pg-modal-header">
                            <h2>{selectedLoan.meta.icon} {selectedLoan.type}</h2>
                            <button className="pg-modal-close" onClick={() => setShowDetails(false)}>×</button>
                        </div>
                        <div className="pg-modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem 1.5rem', marginBottom: '1.25rem' }}>
                                {[
                                    ['Account No.', selectedLoan.account_number],
                                    // ['Loan Amount', `₹${selectedLoan.displayAmount}`],
                                    // ['Total Repayment', `₹${selectedLoan.displayEmi}`],
                                    // ['Interest Rate', `${selectedLoan.interest_rate}% per day`],
                                    // ['Tenure', `${selectedLoan.tenure_months} days`],
                                    // ['Next Due Date', selectedLoan.dueDate],
                                    ['Application Status',
                                        selectedLoan.status === 'Rejected' ? '❌ Rejected'
                                        : (selectedLoan.status === 'Active' || selectedLoan.status === 'Closed') ? `✅ ${selectedLoan.stage_label || 'Disbursed'}`
                                        : (selectedLoan.approval_status === 'Admin_Approved' || selectedLoan.approval_status === 'Disbursed') ? '✅ Approved'
                                        : `🕐 ${selectedLoan.stage_label || 'Under Review'}`],
                                    ['KYC Status', selectedLoan.kyc_status === 'completed' ? '✅ Verified' : '⏳ Pending'],
                                    ['Admin Approval', getApprovalBadge(selectedLoan.approval_status).label],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.68rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '0.15rem' }}>{label}</div>
                                        <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>{val}</div>
                                    </div>
                                ))}
                            </div>

                            {selectedLoan.rejection_reason && (
                                <div className="pg-alert pg-alert-danger" style={{ marginBottom: '1rem' }}>
                                    <span className="pg-alert-icon">⚠️</span>
                                    <div className="pg-alert-body">
                                        <h4>Rejection Reason</h4>
                                        <p>{selectedLoan.rejection_reason}</p>
                                    </div>
                                </div>
                            )}

                            <div className="pg-progress-wrap">
                                <div className="pg-progress-meta">
                                    <span>Loan Progress</span>
                                    <strong>{selectedLoan.progress}%</strong>
                                </div>
                                <div className="pg-progress-track" style={{ height: '12px' }}>
                                    <div className="pg-progress-fill" style={{ width: `${selectedLoan.progress}%` }} />
                                </div>
                            </div>
                        </div>
                        <div className="pg-modal-footer">
                            <button className="pg-btn pg-btn-ghost" onClick={() => setShowDetails(false)}>Close</button>
                            {selectedLoan.status === 'Active' && (
                                <button className="pg-btn pg-btn-primary" onClick={() => { setShowDetails(false); navigate('/dashboard/payments'); }}>
                                    💳 Make Payment
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LoansPage;
